// ---------------------------------------------------------------------------
// Excel-compatible (TSV) row clipboard helpers
//
// Excel exchanges tabular data as tab-separated values; a single row is its
// cells joined by tabs. These helpers serialize a PEF row to that format and
// parse a pasted row back, staying locale-agnostic by deriving the active
// locale's decimal/grouping separators at runtime rather than hard-coding them.
// ---------------------------------------------------------------------------

export interface LocaleSeparators {
  /** Decimal separator for the locale (e.g. ',' for it-IT, '.' for en-US). */
  decimal: string;
  /** Grouping/thousands separator for the locale (e.g. '.' for it-IT). */
  group: string;
}

/** Derives the decimal and grouping separators used by a given locale. */
export function getLocaleSeparators(locale: string): LocaleSeparators {
  const parts = new Intl.NumberFormat(locale).formatToParts(11111.1);
  return {
    group: parts.find((p) => p.type === 'group')?.value ?? ',',
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
  };
}

/**
 * Serializes a row of cell values into a single tab-separated line.
 * Numbers are formatted with the locale's decimal separator and **no grouping**
 * (so the result has at most one separator per number, avoiding ambiguity).
 * `null`/`undefined` become empty cells; strings are emitted verbatim.
 */
export function serializeRowTsv(
  values: ReadonlyArray<number | string | null>,
  locale: string,
): string {
  const fmt = new Intl.NumberFormat(locale, {
    useGrouping: false,
    maximumFractionDigits: 20,
  });
  return values
    .map((v) => {
      if (v === null || v === undefined) return '';
      return typeof v === 'number' ? fmt.format(v) : String(v);
    })
    .join('\t');
}

/**
 * Parses a single token (one Excel cell) into a number, tolerating both the
 * active locale's formatting and common foreign formats.
 *
 * Rules, in order:
 * - When both `.` and `,` are present, the **last-occurring** one is the
 *   decimal separator and the other is grouping (handles "1.234,56" and
 *   "1,234.56" regardless of origin).
 * - When only one separator is present, it is interpreted using the locale:
 *   the locale's decimal separator → decimal point; otherwise → grouping
 *   (so Italian "1.500" → 1500, "0,03" → 0.03; English "1,500" → 1500).
 *
 * Returns `null` for empty/non-numeric tokens.
 */
export function parseLocaleNumber(
  token: string,
  sep: LocaleSeparators,
): number | null {
  // Strip everything except digits, separators and a leading sign.
  let s = token.trim().replace(/[^\d.,-]/g, '');
  if (s === '' || s === '-') return null;

  const hasDot = s.includes('.');
  const hasComma = s.includes(',');

  if (hasDot && hasComma) {
    const decimalChar = s.lastIndexOf('.') > s.lastIndexOf(',') ? '.' : ',';
    const groupChar = decimalChar === '.' ? ',' : '.';
    s = s.split(groupChar).join('').replace(decimalChar, '.');
  } else if (hasComma) {
    s = sep.decimal === ',' ? s.replace(',', '.') : s.split(',').join('');
  } else if (hasDot) {
    if (sep.decimal === '.') {
      // Keep the last dot as the decimal point; any earlier dots are grouping.
      const lastDot = s.lastIndexOf('.');
      s = s.slice(0, lastDot).split('.').join('') + '.' + s.slice(lastDot + 1);
    } else {
      s = s.split('.').join('');
    }
  }

  const num = parseFloat(s);
  return isNaN(num) ? null : num;
}

/**
 * Parses the first line of pasted clipboard text into per-column numeric
 * values. Extra lines (multi-row paste) are ignored — this is a single-row
 * operation.
 */
export function parseRowTsv(
  text: string,
  sep: LocaleSeparators,
): (number | null)[] {
  const firstLine = text.replace(/\r/g, '').split('\n')[0] ?? '';
  return firstLine.split('\t').map((tok) => parseLocaleNumber(tok, sep));
}
